"use client";

import { useMemo } from "react";
import { activityFeedDataLoading, initialActivityFeedData, useActivityFeedData, type ActivityFeedData } from "./use-activity-feed-data";
import { useConvexAuth } from "convex/react";
import type { Id } from "@convex/_generated/dataModel";
import type { LucideIcon } from "@/components/icons";
import {
  FileText,
  LifeBuoy,
  Package,
  ShoppingCart,
  Truck,
  UserCog,
} from "@/components/icons";
import { ACCOUNT_TYPE } from "@convex/constants/types";
import { PROFILE_REQUIREMENT_CODES } from "@convex/profileCompletion/types";
import type { AccountType } from "@/lib/auth/types";
import { formatCappedCount } from "@shared/records/bounded-count";

export type ActivityItem = {
  id: string;
  title: string;
  subtitle: string;
  href?: string;
  icon: LucideIcon;
  priority: 0 | 1 | 2;
};

export interface ActivityFeed {
  items: ActivityItem[];
  actionCount: number;
  isLoading: boolean;
}

const ACTION_COUNT_CAP = 99;

export function formatActionCount(count: number): string {
  return count > ACTION_COUNT_CAP ? `${ACTION_COUNT_CAP}+` : String(count);
}

const VENDOR_ACTION_ICONS: Record<string, LucideIcon> = {
  order: ShoppingCart,
  lead: FileText,
  listing: Package,
  inventory: Package,
};

function payoutSetupSubtitle(
  payouts: { underReview: boolean; canManageBilling: boolean },
  setupDescription: string,
): string {
  if (payouts.underReview) return "Under review";
  return payouts.canManageBilling ? setupDescription : "Ask a billing manager";
}

export function useActivityFeedSource(
  companyId: Id<"companies"> | undefined,
  accountTypes: readonly AccountType[] | undefined,
) {
  const { isAuthenticated, isLoading: authLoading } = useConvexAuth();
  const audienceResolved = accountTypes !== undefined;
  const roles = accountTypes ?? [];
  const isVendor = roles.includes(ACCOUNT_TYPE.VENDOR);
  const isFreight = roles.includes(ACCOUNT_TYPE.FREIGHT_CARRIER);
  const isBuyer =
    roles.includes(ACCOUNT_TYPE.BUYER) ||
    (!audienceResolved && !isVendor && !isFreight);
  const enabled = isAuthenticated && Boolean(companyId);

  const audience = { enabled, isVendor, isBuyer, isFreight, companyId };
  const data = useActivityFeedData(audience);
  return { ...audience, data, isAuthenticated, authLoading, isLoading: activityFeedDataLoading(audience, data) };
}

export function useActivityFeed(
  companyId: Id<"companies"> | undefined,
  accountTypes: readonly AccountType[] | undefined,
): ActivityFeed {
  return useActivityFeedResult(useActivityFeedSource(companyId, accountTypes));
}

export function useActivityFeedResult(
  source: ReturnType<typeof useActivityFeedSource>,
  initialData?: ActivityFeedData,
): ActivityFeed {
  const { isAuthenticated, authLoading, companyId } = source;
  const data = initialActivityFeedData(source.data, initialData);
  const { vendorActionItems, quotedLeads, pendingOrders, carrierCounts, companyReadiness, ticketsAwaiting } = data;
  const isLoading = activityFeedDataLoading(source, data);

  return useMemo<ActivityFeed>(() => {
    if ((!isAuthenticated && (!authLoading || !initialData)) || !companyId) {
      return { items: [], actionCount: 0, isLoading: false };
    }

    const items: ActivityItem[] = [];

    for (const ticket of ticketsAwaiting ?? []) {
      items.push({
        id: `support-${ticket._id}`,
        title: ticket.subject,
        subtitle: "Repackify replied — read & respond",
        href: `/dashboard/support/${ticket._id}`,
        icon: LifeBuoy,
        priority: 0,
      });
    }

    for (const req of companyReadiness?.profile?.requirements ?? []) {
      if (req.completed) continue;
      const payouts =
        req.code === PROFILE_REQUIREMENT_CODES.BILLING_SETUP
          ? companyReadiness?.payouts
          : undefined;
      items.push({
        id: `setup-${req.code}`,
        title: req.label,
        subtitle: payouts ? payoutSetupSubtitle(payouts, req.description) : req.description,
        href: payouts && !payouts.canManageBilling ? undefined : req.actionUrl,
        icon: UserCog,
        priority: req.priority === "required" ? 1 : 2,
      });
    }

    for (const item of vendorActionItems ?? []) {
      items.push({
        id: item.id,
        title: item.title,
        subtitle: item.subtitle,
        href: item.href,
        icon: VENDOR_ACTION_ICONS[item.type] ?? FileText,
        priority:
          item.priority === "urgent" ? 0 : item.priority === "high" ? 1 : 2,
      });
    }

    for (const lead of quotedLeads ?? []) {
      if (lead.quoteCount === 0) continue;
      items.push({
        id: `lead-${lead._id}`,
        title: `Request #${lead.leadNumber || lead._id.slice(-6)}`,
        subtitle: `${formatCappedCount(lead.quoteCount, lead.quoteCountIsCapped)} quote${lead.quoteCount === 1 && !lead.quoteCountIsCapped ? "" : "s"} to review`,
        href: `/dashboard/orders/${lead._id}`,
        icon: FileText,
        priority: 1,
      });
    }

    for (const order of pendingOrders?.orders ?? []) {
      items.push({
        id: `order-${order._id}`,
        title: `Order #${order.orderNumber || order._id.slice(-6)}`,
        subtitle: "Pending — awaiting confirmation",
        href: `/dashboard/orders/${order._id}`,
        icon: ShoppingCart,
        priority: 1,
      });
    }

    const pendingBids = carrierCounts?.pendingBids;
    if (pendingBids && pendingBids.count > 0) {
      items.push({
        id: "carrier-bids",
        title: `${formatCappedCount(pendingBids.count, pendingBids.isCapped)} quote${pendingBids.count === 1 && !pendingBids.isCapped ? "" : "s"} awaiting your bid`,
        subtitle: "Review and respond to freight requests",
        href: "/dashboard/carrier/bids",
        icon: Truck,
        priority: 1,
      });
    }

    items.sort((left, right) => left.priority - right.priority);

    return {
      items,
      actionCount: items.length,
      isLoading,
    };
  }, [
    isAuthenticated,
    initialData,
    companyId,
    authLoading,
    isLoading,
    vendorActionItems,
    quotedLeads,
    pendingOrders,
    carrierCounts,
    companyReadiness,
    ticketsAwaiting,
  ]);
}
